import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import { OnlineQuoteToolStore } from '../store';
import { DeeplinkGeneratorService as SharedDeeplinkService } from '@lx/quote-tool/lite/shared/services';
import { DeeplinkTypeEnum, PreselectedModeEnum } from '@lx/quote-tool/lite/shared/types';
import { OnlineQuoteInputMapper } from './quote-input.mapper';

/**
 * Online Quote Tool deeplink generator
 * Uses shared service for API calls and provides app-specific validation
 * Builds deeplink payload matching quote-tool-lite structure
 */
@Injectable({ providedIn: 'root' })
export class OnlineDeeplinkGeneratorService {
  private readonly store = inject(OnlineQuoteToolStore);
  /**
   * Observable that emits whether the current state is valid for generating a deeplink
   */
  readonly valid$: Observable<boolean> = this.store.deeplinkSelectionData$.pipe(
    map(
      ({ residenceCountryCode, selectedSchool, selectedCourse, selectedDuration }) =>
        !!(residenceCountryCode && selectedSchool && selectedCourse?.code && selectedDuration),
    ),
  );
  private readonly sharedDeeplinkService = inject(SharedDeeplinkService);
  private readonly onlineQuoteInputMapper = inject(OnlineQuoteInputMapper);

  /**
   * Generates a deeplink based on the current state
   * Uses shared service to call the backend API
   * @returns Observable that emits the deeplink URL or null if generation fails
   */
  generateDeeplink(): Observable<string | null> {
    return this.store.deeplinkSelectionData$.pipe(
      switchMap(({ residenceCountryCode, selectedSchool, selectedCourse, selectedDuration, selectedStartDate }) => {
        // Build request matching quote-tool-lite structure for online flow
        const quoteInput = this.onlineQuoteInputMapper.mapQuoteInput({
          residenceCountryCode,
          selectedSchool,
          selectedCourse,
          selectedDuration,
          selectedStartDate,
        });
        return this.sharedDeeplinkService.generateDeeplink({
          quoteInput,
          preselectedMode: PreselectedModeEnum.ONLINE_COURSE,
          deeplinkType: DeeplinkTypeEnum.COPY_SELECTION,
        });
      }),
    );
  }
}
